import { Selo } from "@plantus/ui";

import type { Habilitacao } from "./tipos";

interface Props {
  itens: Habilitacao[];
  /**
   * O nome fantasia da empresa, a partir do <b>slug</b>.
   *
   * <p>Chega como função porque o bloco <b>não pode ter uma lista de empresas</b>:
   * ela existia aqui ao lado, fixa, e era a segunda fonte da verdade sobre as
   * três pessoas jurídicas (#24). Quem resolve o nome é quem tem a lista da API
   * — a página —, e este bloco só sabe pedir.
   */
  nomeDe(slug: string): string;
  /** O mesmo, na forma curta que cabe na frase do risco herdado. */
  apelidoDe(slug: string): string;
}

/**
 * As habilitações — e quem para junto se cada uma cair.
 *
 * <p>É o bloco que justifica o painel existir. A Plantus Óleos e Extratos opera
 * sob o alvará da ProLab por contrato de locação: <b>se o alvará ou o contrato
 * vencer, ela para.</b> Hoje ninguém monitora essa dependência.
 *
 * <p>Mostrar só a data de vencimento esconderia metade da consequência — quem
 * lesse "alvará da ProLab vence em 87 dias" não teria por que pensar na outra
 * empresa. Por isso <b>risco herdado</b> aparece explícito, com o nome de quem
 * herda.
 */
export function BlocoDeHabilitacoes({ itens, nomeDe, apelidoDe }: Props) {
  if (itens.length === 0) {
    return (
      <p className="painel__vazio">
        Nenhuma habilitação cadastrada. Aqui aparecerão AFE, alvará sanitário e
        contratos de locação — com o prazo de cada um e quais empresas param se
        ele cair.
      </p>
    );
  }

  return (
    <ul className="painel__lista">
      {itens.map((item) => {
        const herdeiros = item.quemDepende.filter((e) => e !== item.titular);

        return (
          <li key={item.id} className="habilitacao">
            <div className="habilitacao__cabeca">
              <span className="habilitacao__tipo">{item.tipo}</span>
              <Selo situacao={item.situacao} />
            </div>

            <p className="habilitacao__titular">{nomeDe(item.titular)}</p>

            <p className="habilitacao__prazo">
              {item.diasParaVencer === null
                ? "Sem prazo — processo em curso"
                : `Vence em ${item.diasParaVencer} dias`}
            </p>

            {/* O painel diz o que fazer, não só o que está errado. */}
            <p className="habilitacao__acao">{item.acao}</p>

            {herdeiros.length > 0 && (
              <p className="habilitacao__herdado">
                <strong>Risco herdado:</strong>{" "}
                {herdeiros.map((e) => apelidoDe(e)).join(", ")} opera sob esta
                habilitação e para junto.
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
